import { useMount } from 'ahooks';
import { Tooltip } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { RevxSkeleton } from 'components';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { toast } from 'components/RevxMessage';
import { RevxTrackingInfo } from 'components/RevxTrackingInfo';
import { UnderlineText } from 'components/RevxTypography';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import {
  FileItem,
  ProofItem,
  ProofStatusEnum,
  ProofTab,
  ProofTypeEnum,
} from 'domains/workspaceDetailDomain';
import { JobItem, JobWorkflowStatusEnum } from 'domains/workspaceDomain';
import { useJobStatusCollection } from 'hooks/useJobStatusCollection';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { revxScrollTo } from 'utils/scroll';
import { CommonProofNotes, CommonUploadFile } from '../../common';
import { ProofsOpenNote } from '../../common/ProofNotes';
import './Proof.less';

const Proof = (jobData: JobItem) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const [proofFileList, setProofFileList] = useState<FileItem[]>([]);
  const [proofList, setProofList] = useState<ProofItem[]>([]);
  const statusFilters = useJobStatusCollection();
  const [loading, setLoading] = useState(false);
  const [choiceProof, setchoiceProof] = useState<ProofItem>();
  const [viewNotes, setViewNotes] = useState<boolean>(false);
  const [isDeniedApprovalModal, setIsDeniedApprovalModal] =
    useState<boolean>(false);
  const commonNoteRef = useRef<ProofsOpenNote>(null);

  const proofTabList: Partial<ProofTab> = useMemo(() => {
    const tabList: Partial<ProofTab> = {};
    tabList[ProofTypeEnum.DIGITAL] = proofList
      .filter(
        (item) =>
          item.proofType === ProofTypeEnum.DIGITAL &&
          item.proofStatus !== ProofStatusEnum.DRAFT
      )
      .reverse();
    tabList[ProofTypeEnum.HARD_COPY] = proofList
      .filter(
        (item) =>
          item.proofType === ProofTypeEnum.HARD_COPY &&
          item.proofStatus !== ProofStatusEnum.DRAFT
      )
      .reverse();
    if (tabList[ProofTypeEnum.DIGITAL]?.length) {
      setchoiceProof(tabList[ProofTypeEnum.DIGITAL]?.slice(0)[0]);
    } else {
      setchoiceProof(tabList[ProofTypeEnum.HARD_COPY]?.slice(0)[0]);
    }
    setViewNotes(false);
    return tabList;
  }, [proofList]);

  const supplierProofingStatus = useMemo(() => {
    return {
      showDigital: !!proofTabList[ProofTypeEnum.DIGITAL]?.length,
      showHardcopy: !!proofTabList[ProofTypeEnum.HARD_COPY]?.length,
      awaitProofing:
        !proofTabList[ProofTypeEnum.HARD_COPY]?.length &&
        !proofTabList[ProofTypeEnum.DIGITAL]?.length,
    };
  }, [proofTabList]);

  const proofActionStatus = useMemo(() => {
    const choiceFileDisabled =
      choiceProof?.proofStatus !== ProofStatusEnum.WAITING_APPROVE;
    const showProofActionApprove =
      choiceProof?.proofStatus === ProofStatusEnum.APPROVE;
    const showProofActionDecline =
      choiceProof?.proofStatus === ProofStatusEnum.DECLINED;
    return {
      choiceFileDisabled,
      showProofActionApprove,
      showProofActionDecline,
      proofActionFinish: showProofActionApprove || showProofActionDecline,
    };
  }, [choiceProof]);

  const defaultProofList = useCallback(async () => {
    setLoading(true);
    const proofListRes = await dispatch.workspaceDetail.getProofAll(jobData);
    setProofList(proofListRes);
    setLoading(false);
    return proofListRes;
  }, [dispatch.workspaceDetail, jobData]);

  useMount(async () => {
    if (jobData.digitalProofRequired || jobData.hardcopyProofRequired) {
      defaultProofList();
      dispatch.workspace.getJobItemCount(jobData.id);
    }
  });

  const defaultProofFile = useCallback(async () => {
    if (choiceProof) {
      const fileListRes = await dispatch.workspaceDetail.getAllFileProof(
        choiceProof
      );
      setProofFileList(fileListRes);
    }
  }, [choiceProof, dispatch.workspaceDetail]);

  const defaultUpdateJobItem = useCallback(async () => {
    dispatch.workspace.getJobItemCount(jobData.id);
  }, [dispatch.workspace, jobData]);

  useEffect(() => {
    defaultProofFile();
  }, [defaultProofFile]);

  useEffect(() => {
    if (isDeniedApprovalModal) {
      setViewNotes(true);
    }
  }, [isDeniedApprovalModal]);

  useEffect(() => {
    if (!viewNotes) {
      setIsDeniedApprovalModal(false);
    }
  }, [viewNotes]);

  const openNotes = (e: React.MouseEvent<HTMLElement>) => {
    setIsDeniedApprovalModal(true);
    commonNoteRef.current?.openNoteHandler();
    revxScrollTo(e.pageY - 200);
  };

  const declineHandle = async () => {
    setLoading(true);
    choiceProof && (await dispatch.workspaceDetail.buyerDecline(choiceProof));
    defaultProofList();
    setLoading(false);
    defaultUpdateJobItem();
  };

  const approveHandle = async () => {
    setLoading(true);
    choiceProof && (await dispatch.workspaceDetail.buyerApprove(choiceProof));
    toast(t('toast.buyerProofApproved'));

    defaultProofList();
    dispatch.workspace.getJobCountByStatusFilter(statusFilters);
    dispatch.workspace.reloadJobList();
    setLoading(false);
  };

  const digitalProof = () => (
    <>
      <p className="buyer-proof-card-approve-title">
        {t('workspace.detail.proofs.digitalProofApproval')}
      </p>
      <div
        className="buyer-proof-card-shipping"
        style={{ borderBottom: 'none' }}
      >
        <span hidden={ProofStatusEnum.DRAFT === choiceProof?.proofStatus}>
          {t('workspace.detail.proofs.edits')}:
          {`  (${choiceProof?.proofMsg.length})`}
        </span>
        <Tooltip title={choiceProof?.proofMsg[0]?.msg}>
          <UnderlineText
            color="blue"
            size="small"
            style={{ paddingLeft: 10 }}
            ellipsis
            onClick={() => setViewNotes(!viewNotes)}
          >
            {t('workspace.detail.proofs.viewNotes')}
          </UnderlineText>
        </Tooltip>
      </div>
      <RevxFlexLayout align="top">
        <p
          className="buyer-proof-card-approve-title"
          hidden={proofTabList[ProofTypeEnum.DIGITAL]?.length === 1}
        >
          {t('workspace.detail.proofs.proof')}:
        </p>
        <p
          className="buyer-proof-card-approve-title"
          style={{ lineHeight: '40px' }}
          hidden={proofTabList[ProofTypeEnum.DIGITAL]?.length !== 1}
        >
          {t('workspace.detail.proofs.proof')}:
        </p>
        <div>
          <p
            className="buyer-proof-card-version"
            hidden={proofTabList[ProofTypeEnum.DIGITAL]?.length === 1}
          >
            {proofTabList[ProofTypeEnum.DIGITAL]?.map((item) => (
              <React.Fragment key={item.id}>
                {choiceProof?.id !== item.id ? (
                  <a
                    onClick={() => {
                      setchoiceProof(item);
                      setViewNotes(false);
                    }}
                  >
                    {`Version ${item.proofVersion}`}
                  </a>
                ) : (
                  <span>{`Version ${choiceProof?.proofVersion}`}</span>
                )}
              </React.Fragment>
            ))}
          </p>
          {Boolean(proofFileList.length) ? (
            <div
              className="buyer-proof-card-shipping"
              style={{ borderBottom: 'none', marginBottom: `-24px` }}
            >
              <span>{t('workspace.detail.proofs.reviewDigitalProof')}:</span>
            </div>
          ) : (
            <></>
          )}
          <CommonUploadFile defaultFileList={proofFileList} />
        </div>
      </RevxFlexLayout>

      <div className="buyer-proof-card-approve">
        <p
          hidden={proofActionStatus.proofActionFinish}
          className="buyer-proof-card-approve-title"
        >
          {t('workspace.detail.proofs.approveProofTitle')}
        </p>
        <div className="buyer-proof-card-approve-container">
          <p hidden={proofActionStatus.proofActionFinish}>
            {t('workspace.detail.proofs.approveProofTip')}
          </p>
          <div className="buyer-proof-card-approve-container-action">
            {!proofActionStatus.showProofActionApprove && (
              <RevxButton
                className="no-approval"
                onClick={(e) => openNotes(e)}
                disabled={proofActionStatus.choiceFileDisabled}
              >
                {t('workspace.detail.proofs.noApprovalButton')}
              </RevxButton>
            )}
            {!proofActionStatus.showProofActionDecline && (
              <RevxButton
                type="primary"
                className="approve-proof"
                onClick={() => approveHandle()}
                disabled={proofActionStatus.choiceFileDisabled}
              >
                {t('workspace.detail.proofs.approveProofButton')}
              </RevxButton>
            )}
          </div>
        </div>
      </div>
    </>
  );

  const hardcopyProof = () => (
    <>
      <p className="buyer-proof-card-approve-title">
        {t('workspace.detail.proofs.hardcopyProof')}
      </p>
      <RevxFlexLayout align="top">
        <div>
          <p
            className="buyer-proof-card-approve-title"
            hidden={proofTabList[ProofTypeEnum.HARD_COPY]?.length === 1}
          >
            {t('workspace.detail.proofs.proof')}:
          </p>
          <p
            className="buyer-proof-card-approve-title"
            style={{ lineHeight: '44px' }}
            hidden={proofTabList[ProofTypeEnum.HARD_COPY]?.length !== 1}
          >
            {t('workspace.detail.proofs.proof')}:
          </p>
        </div>
        <div>
          <p
            className="buyer-proof-card-version"
            hidden={proofTabList[ProofTypeEnum.HARD_COPY]?.length === 1}
          >
            {/* <span>{t('workspace.detail.proofs.proof')}:</span> */}
            {proofTabList[ProofTypeEnum.HARD_COPY]?.map((item) => (
              <React.Fragment key={item.id}>
                {choiceProof?.id !== item.id ? (
                  <a
                    onClick={() => {
                      setchoiceProof(item);
                      setProofFileList([]);
                      setViewNotes(false);
                    }}
                  >
                    {`Version ${item.proofVersion}`}
                  </a>
                ) : (
                  <span>{`Version ${item.proofVersion}`}</span>
                )}
              </React.Fragment>
            ))}
          </p>
          <div className="buyer-proof-card-address">
            <span>{t('workspace.detail.proofs.sendProofTo')}:</span>
            {[
              choiceProof?.toName,
              choiceProof?.toAddressLine,
              `${choiceProof?.toCity}, ${choiceProof?.toStateProvinceCode}, ${choiceProof?.toPostalCode}`,
            ]
              .filter((item) => item)
              .map((item) => (
                <p key={item}>{item}</p>
              ))}
          </div>
          <div className="buyer-proof-card-shipping">
            <span>{t('workspace.detail.proofs.shippingTracking')}:</span>
            <a>
              <RevxTrackingInfo
                carrierType={'UPS' as CarrierTypeEnum}
                size="default"
                value={choiceProof?.trackingCode || ''}
              />
            </a>
            <span>
              {t('workspace.detail.proofs.editsReceived')}:
              {ProofStatusEnum.DRAFT !== choiceProof?.proofStatus
                ? `  (${choiceProof?.proofMsg.length})`
                : ''}
            </span>
            {Boolean(choiceProof?.proofMsg.length) ||
            (choiceProof?.proofStatus &&
              [ProofStatusEnum.DRAFT, ProofStatusEnum.WAITING_APPROVE].includes(
                choiceProof?.proofStatus
              )) ? (
              <>
                <Tooltip title={choiceProof?.proofMsg[0]?.msg}>
                  <UnderlineText
                    color="blue"
                    size="small"
                    style={{ paddingLeft: 10 }}
                    ellipsis
                    onClick={() => setViewNotes(!viewNotes)}
                  >
                    {t('workspace.detail.proofs.viewNotes')}
                  </UnderlineText>
                </Tooltip>
              </>
            ) : (
              <></>
            )}
          </div>
          {Boolean(proofFileList.length) ? (
            <div
              className="buyer-proof-card-shipping"
              style={{ borderBottom: 'none', marginBottom: `-24px` }}
            >
              <span>{t('workspace.detail.proofs.reviewDigitalProof')}:</span>
            </div>
          ) : (
            <></>
          )}
          {!!proofFileList.length && (
            <CommonUploadFile defaultFileList={proofFileList} />
          )}
        </div>
      </RevxFlexLayout>

      <div
        className={`buyer-proof-card-approve ${
          !proofFileList.length ? 'buyer-proof-card-fileNone' : ''
        }`}
      >
        <p
          className="buyer-proof-card-approve-title"
          hidden={proofActionStatus.choiceFileDisabled}
        >
          {t('workspace.detail.proofs.approveProofTitle')}
        </p>
        <div className="buyer-proof-card-approve-container">
          <p hidden={proofActionStatus.choiceFileDisabled}>
            {t('workspace.detail.proofs.approveProofTip')}
          </p>
          <div className="buyer-proof-card-approve-container-action">
            {!proofActionStatus.showProofActionApprove && (
              <RevxButton
                className="no-approval"
                disabled={proofActionStatus.choiceFileDisabled}
                onClick={(e) => openNotes(e)}
                hidden={proofActionStatus.showProofActionApprove}
              >
                {t('workspace.detail.proofs.noApprovalButton')}
              </RevxButton>
            )}
            {!proofActionStatus.showProofActionDecline && (
              <RevxButton
                type="primary"
                className="approve-proof"
                disabled={proofActionStatus.choiceFileDisabled}
                onClick={() => approveHandle()}
              >
                {proofActionStatus.choiceFileDisabled
                  ? t('workspace.detail.proofs.approvedButton')
                  : t('workspace.detail.proofs.approveProofButton')}
              </RevxButton>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="buyer-proof-card">
      {jobData.digitalProofRequired || jobData.hardcopyProofRequired ? (
        <RevxSkeleton loading={loading}>
          {supplierProofingStatus.awaitProofing && (
            <p className="buyer-proof-card-upload-title buyer-proof-card-upload-title-await-proof">
              {t('workspace.detail.proofs.awaitingSupplierProofing')}
            </p>
          )}
          {Boolean(!supplierProofingStatus.awaitProofing) ? (
            <>
              {Boolean(supplierProofingStatus.showDigital) && digitalProof()}
              {Boolean(supplierProofingStatus.showHardcopy) && hardcopyProof()}
              {choiceProof && viewNotes && (
                <CommonProofNotes
                  ref={commonNoteRef}
                  choiceProof={choiceProof}
                  defaultProofList={defaultProofList}
                  isDeniedApprovalModal={isDeniedApprovalModal}
                  deniedApprovalHandler={declineHandle}
                  hideAction={
                    ![
                      JobWorkflowStatusEnum.PREPARE_PROOF,
                      JobWorkflowStatusEnum.PROOF_AWAIT_APPROVAL,
                      JobWorkflowStatusEnum.PROOF_NEED_REVISION,
                    ].includes(jobData.workflowStatus)
                  }
                />
              )}
            </>
          ) : (
            <></>
          )}
        </RevxSkeleton>
      ) : (
        <div className="buyer-proof-card-approve-title">No proof required.</div>
      )}
    </div>
  );
};

export default Proof;
